import React, { useEffect, useState } from 'react';

interface ReferralDiscountReasonModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  savingLabel: string;
  placeholder: string;
  isSaving: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const ReferralDiscountReasonModal: React.FC<ReferralDiscountReasonModalProps> = ({
  isOpen,
  title,
  description,
  confirmLabel,
  savingLabel,
  placeholder,
  isSaving,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isSaving) return;
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, isSaving, onCancel]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!reason.trim()) {
      setError('A reason is required');
      return;
    }

    onConfirm(reason.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div role="dialog" aria-modal="true" aria-labelledby="referral-discount-reason-title" className="bg-white px-4 py-6 md:p-6 rounded-md shadow-lg max-w-md w-full">
        <h2 id="referral-discount-reason-title" className="text-2xl font-bold mb-4">{title}</h2>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="referral-discount-reason" className="block text-gray-700 mb-1">
              Reason
            </label>
            <input
              id="referral-discount-reason"
              type="text"
              value={reason}
              onChange={e => {
                setReason(e.target.value);
                if (error) setError('');
              }}
              placeholder={placeholder}
              className="w-full px-3 py-2 border rounded"
              maxLength={200}
              autoFocus
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-4 py-2 rounded">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50"
            >
              {isSaving ? savingLabel : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReferralDiscountReasonModal;
